import { NUMBER_WORDS, figureSet, formatCents } from "./figures";
import type { PersonaSynthesis } from "./personas";
import { fitBetaPrior, shrinkRate, type GroupCount } from "./shrinkage";

/**
 * W1 ANALYST (Agent Brains 13 and 5.7, Training and Auto-Learn Master Spec B7,
 * B13). The pure half of the weekly digest and the Q&A box. No IO, no model.
 *
 * THE RULES THIS FILE CARRIES
 *  - Numbers come from deterministic code (CLAUDE.md section 4). A count, a
 *    cents sum or a rate is computed from rows by a function here or in
 *    `analystQueries.ts`; the model, where one is used at all, only phrases
 *    prose from the figures it is handed, and `proseFigureViolations` throws
 *    the prose out if it states one that was not handed over.
 *  - Precision beats recall. A cents sum or a rate over fewer than
 *    `ANALYST_SAMPLE_FLOOR` rows reads `not_measured`, never zero and never a
 *    small number dressed as a finding. A count is a fact whatever its size.
 *  - "Assisted" and "association", never lift. The data cannot say what would
 *    have happened without the agent, so no line here claims a causal effect.
 *  - Hypotheses are labelled, apart from the measured lines, with their n.
 *  - Nothing here recommends money off (CLAUDE.md section 0.5): a prose that
 *    names an offer is discarded like one that invents a figure.
 */

/**
 * The fewest rows a cents sum or a rate needs before it is shown as a figure.
 * AWAITING RATIFICATION (Kabir). 5 is the repo's existing small-n bar for a
 * single-client claim (F-15); this is a separate copy on purpose, so changing
 * the analyst's floor never moves the learner's.
 */
export const ANALYST_SAMPLE_FLOOR = 5;

/**
 * Free answers per client per ISO week before `premium_required`. Design open
 * question 11. AWAITING RATIFICATION (Kabir). Billing itself is Y5.
 */
export const ANALYST_FREE_ASKS_PER_WEEK = 2;
// Every ask that costs a paid model call spends one of them: an answer, but also a clarify
// and an unmapped, since each of those cost a map call (W1 fix 1, minor 6). AWAITING
// RATIFICATION (Kabir): whether a clarify or an unmapped ask should be free after all.

/** Trailing window the splits are read over, in days. AWAITING RATIFICATION (Kabir). */
export const ANALYST_TRAILING_DAYS = 28;

/** The longest question the router or the mapping call reads. AWAITING RATIFICATION. */
export const ANALYST_QUESTION_MAX = 500;

/**
 * The most cells of one section the `digest.analyst` row stores, so the owner can read the
 * splits later without a rerun. The row's `rows` count still says how many there were.
 * AWAITING RATIFICATION.
 */
export const ANALYST_SECTION_CELLS_MAX = 40;

/** The most cells of one section the one-line mail carries. AWAITING RATIFICATION. */
export const ANALYST_MAIL_CELLS_MAX = 6;

/** The longest phrased answer kept. AWAITING RATIFICATION. */
export const ANALYST_ASK_PROSE_MAX = 600;

export const ANALYST_QUERY_IDS = [
  "deposits_by_inquiry_type",
  "consults_by_week",
  "assisted_revenue_by_agent",
  "approval_rate_by_agent_scenario",
  "stage_conversion",
  "knowledge_gaps",
  "learner_changes",
  "restraint_outcomes",
  "scoreboard",
  // G54: the sales measure's first response and entry attribution, read through `measureSales`.
  "response_time",
  "entry_sources",
  // G25: counts per rating per closed week from platform_reviews; empty until a review provider exists.
  "review_movement",
  // TA-1336: what customers say other shops and sites charge, per piece type, against the shop's own range.
  // Owner-facing advice only: it never moves a price (T3) and never recommends money off (section 0.5).
  "competitor_prices",
] as const;
export type AnalystQueryId = (typeof ANALYST_QUERY_IDS)[number];

/** The title the digest and the /digest card show for each section. "Release rate": an edited approval still counts as released (F-05's metric is a different one). */
export const ANALYST_SECTION_TITLES: Readonly<Record<AnalystQueryId, string>> = {
  deposits_by_inquiry_type: "Deposits by inquiry type",
  consults_by_week: "Consults held by week",
  assisted_revenue_by_agent: "Assisted revenue by agent",
  approval_rate_by_agent_scenario: "Release rate by agent and scenario",
  stage_conversion: "Stage conversion",
  knowledge_gaps: "Knowledge gaps",
  learner_changes: "Learner changes",
  restraint_outcomes: "Restraint outcomes",
  scoreboard: "Scoreboard",
  response_time: "First response time",
  entry_sources: "Entry sources",
  review_movement: "Review movement by week",
  competitor_prices: "What customers say other places charge",
};

export function isAnalystQueryId(v: unknown): v is AnalystQueryId {
  return typeof v === "string" && (ANALYST_QUERY_IDS as readonly string[]).includes(v);
}

export type AggregateUnit = "count" | "cents" | "percent";

/**
 * One cell of an aggregate. `n` is the sample the value rests on. `value` is
 * null exactly when `status` is `not_measured`.
 */
export interface AggregateRow {
  readonly key: string;
  readonly n: number;
  readonly value: number | null;
  readonly unit: AggregateUnit;
  readonly status: "measured" | "not_measured";
}

/** `association` for anything an agent was present for; `count` for a plain tally. Never lift. */
export type AggregateLabel = "association" | "count";

export interface QueryResult {
  readonly queryId: AnalystQueryId;
  readonly rows: readonly AggregateRow[];
  /** The sample behind the whole answer. */
  readonly n: number;
  readonly label: AggregateLabel;
}

export function measuredRow(key: string, n: number, value: number | null, unit: AggregateUnit): AggregateRow {
  if (unit === "count") return { key, n, value: value ?? 0, unit, status: "measured" };
  if (value === null || n < ANALYST_SAMPLE_FLOOR) return { key, n, value: null, unit, status: "not_measured" };
  return { key, n, value, unit, status: "measured" };
}

/** Whole percent, half up. Null when there is nothing to divide by: no sample is not 0%. */
export function ratePercent(numerator: number, denominator: number): number | null {
  if (denominator <= 0) return null;
  return Math.floor((numerator * 100) / denominator + 0.5);
}

function valueText(r: AggregateRow): string {
  if (r.status === "not_measured" || r.value === null) return `not measured (n=${r.n}, needs ${ANALYST_SAMPLE_FLOOR})`;
  const v = r.unit === "cents" ? formatCents(r.value) : r.unit === "percent" ? `${r.value}%` : String(r.value);
  return `${v} (n=${r.n})`;
}

/** `key: value (n=N)`. A not_measured cell never renders as a zero. */
export function renderRow(r: AggregateRow): string {
  return `${r.key}: ${valueText(r)}`;
}

/**
 * Every figure the rows carry, in the shop's own formats: the text the model is
 * shown AND the set its prose is checked against. One source for both, so a
 * figure the model can see is exactly a figure it may state.
 */
export function figureTextOf(rows: readonly AggregateRow[], extra: { readonly periodDays?: number } = {}): string {
  const lines = rows.map(renderRow);
  if (extra.periodDays !== undefined) lines.push(`period: ${extra.periodDays} days`);
  return lines.join("\n");
}

// ------------------------------------------------------------------ the router

const KEYWORDS: Readonly<Record<AnalystQueryId, readonly string[]>> = {
  deposits_by_inquiry_type: ["deposit", "inquiry type", "enquiry type"],
  consults_by_week: ["consult", "appointment"],
  assisted_revenue_by_agent: ["revenue", "assisted", "influenced", "dollars", "money"],
  approval_rate_by_agent_scenario: ["approv", "release rate", "edit rate", "rejected"],
  stage_conversion: ["stage", "conversion", "funnel", "convert"],
  knowledge_gaps: ["knowledge gap", "gaps", "could not answer", "unanswered"],
  learner_changes: ["learner", "learned", "proposal", "auto-learn", "autolearn"],
  restraint_outcomes: ["restraint", "withheld", "held back", "silence"],
  scoreboard: ["scoreboard", "score board", "trend"],
  // Plan 2 queries: phrases or nouns an owner's own verbs do not collide with.
  // A bare "review", "post", "story" or "respond" is left out on purpose: "did
  // I review the drafts", "deposits were posted", "deposit history" and
  // "corresponding" would otherwise pull a question onto the wrong report
  // (TA-1263 fix, CLAUDE.md sections 0 and 4).
  response_time: ["response time", "first response", "reply time", "time to respond", "time to reply", "how fast", "how quickly"],
  entry_sources: ["entry source", "reel", "instagram story", "instagram stories", "ig story", "ig stories", "came from"],
  review_movement: ["google review", "reviews", "star rating", "rating", "stars"],
  // TA-1336. Nouns only; a bare "price" or "charge" would pull a deposit question onto this report.
  competitor_prices: ["competitor", "competition", "other jeweler", "other jewellers", "other shops", "other stores", "other places", "compare to other"],
};

function escapeForRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Each keyword matches only where a word starts, so "rating" never fires inside
 * "operating" and "story" never inside "history". The tail stays open, so
 * "deposit" still reads "deposits" and "approv" still reads "approved".
 */
const KEYWORD_PATTERNS: Readonly<Record<AnalystQueryId, readonly RegExp[]>> = (() => {
  const out = {} as Record<AnalystQueryId, readonly RegExp[]>;
  for (const id of ANALYST_QUERY_IDS) out[id] = KEYWORDS[id].map((k) => new RegExp(`(?<![a-z0-9])${escapeForRegExp(k)}`));
  return out;
})();

export type RouteGuess = { readonly kind: "hit"; readonly queryId: AnalystQueryId } | { readonly kind: "none" };

/**
 * The keyword router (design: "keyword router first"). A single query with the
 * most hits is a hit. No hit and a tie at the top are both a miss, handed to
 * the one mapping call: a coin flip between two readings is the recall habit
 * this system refuses (CLAUDE.md section 0).
 */
export function routeQuestion(question: string): RouteGuess {
  const q = question.trim().toLowerCase();
  if (q === "" || q.length > ANALYST_QUESTION_MAX) return { kind: "none" };
  const scored = ANALYST_QUERY_IDS.map((id) => ({ id, hits: KEYWORD_PATTERNS[id].filter((k) => k.test(q)).length }));
  const top = Math.max(...scored.map((s) => s.hits));
  if (top === 0) return { kind: "none" };
  const best = scored.filter((s) => s.hits === top);
  const only = best[0];
  return best.length === 1 && only !== undefined ? { kind: "hit", queryId: only.id } : { kind: "none" };
}

/** The free-ask rule. `used` is the answers already given this ISO week. */
export function analystAskDecision(used: number): { readonly allowed: true } | { readonly allowed: false; readonly used: number } {
  return used < ANALYST_FREE_ASKS_PER_WEEK ? { allowed: true } : { allowed: false, used };
}

// ----------------------------------------------------------- the prose check

/** Words an owner-facing analysis has no business using (CLAUDE.md section 0.5): it never advises money off. */
const OFFER_WORDS_IN_PROSE = /\b(?:discounts?|discounted|promos?|promotions?|promotional|coupons?|sales?|deals?|offers?|offered|offering|bargains?|specials?|cheap(?:er|est)?|free|savings?|saves?)\b|%\s*off\b|\bper\s?cent\s+off\b|\bpercent\s+off\b|\blimited[\s-]+time\b/i;

const SPELLED_NUMBER_IN_PROSE = new RegExp(`\\b(?:${NUMBER_WORDS.join("|")})\\b`, "i");

const NUMERIC_TOKEN = /\d[\d,]*(?:\.\d+)?/g;

/** "4,500.00" and "4500" are one number; text, not Number(), so a long digit run never collapses to a float. */
function foldToken(t: string): string {
  const s = t.replace(/,/g, "");
  return s.includes(".") ? s.replace(/0+$/, "").replace(/\.$/, "") : s;
}

function numericTokens(text: string): Set<string> {
  return new Set((text.match(NUMERIC_TOKEN) ?? []).map(foldToken));
}

/**
 * Why prose may not be shown: an empty list means it may. The checks:
 *  - length and blankness;
 *  - an offer word (section 0.5);
 *  - a spelled number, which the figure reader cannot ground against the rows;
 *  - any digit run the figure text does not carry (so a tampered $4,800.00 or a
 *    plausible-sounding 80% is caught), and any figure `figureSet` (the repo's
 *    one figure reader) finds in the prose that it does not find in the text.
 * Codes only, never the prose itself.
 */
export function proseFigureViolations(prose: string, figureText: string): string[] {
  const out: string[] = [];
  if (prose.trim() === "") out.push("empty");
  if (prose.length > ANALYST_ASK_PROSE_MAX) out.push("too_long");
  const offer = OFFER_WORDS_IN_PROSE.exec(prose);
  if (offer !== null) out.push(`offer_word:${offer[0].toLowerCase()}`);
  const spelled = SPELLED_NUMBER_IN_PROSE.exec(prose);
  if (spelled !== null) out.push(`spelled_number:${spelled[0].toLowerCase()}`);
  const given = numericTokens(figureText);
  for (const t of numericTokens(prose)) if (!given.has(t)) out.push(`figure_not_given:${t}`);
  const givenFigures = figureSet(figureText);
  for (const f of figureSet(prose)) if (!givenFigures.has(f)) out.push(`figure_not_given:${f}`);
  return [...new Set(out)];
}

// ------------------------------------------------------------ template answer

const BASIS: Readonly<Record<AggregateLabel, string>> = {
  association: "Basis: assisted, an association, not a measured effect.",
  count: "Basis: a count of recorded rows.",
};

/** The fixed answer a failed or absent prose falls back to. Deterministic. */
export function templateAnswer(queryId: AnalystQueryId, rows: readonly AggregateRow[], label: AggregateLabel): string {
  const body = rows.length === 0 ? "No rows in the period." : rows.map(renderRow).join("; ");
  return `${queryId}: ${body}. ${BASIS[label]}`;
}

// ------------------------------------------------------------- hypotheses

/** The rules a hypothesis can come from. A code, so the stored row carries no sentence. */
export const ANALYST_HYPOTHESIS_KINDS = ["lowest_release", "largest_stage_drop"] as const;
export type AnalystHypothesisKind = (typeof ANALYST_HYPOTHESIS_KINDS)[number];

/** The most hypothesis cells the `digest.analyst` row stores (G49). */
// AWAITING RATIFICATION
export const ANALYST_HYPOTHESES_MAX = 8;

/** One hypothesis as the row stores it (G49): which rule, which cell, its percent and its n. No free text. */
export interface HypothesisCell {
  readonly kind: AnalystHypothesisKind;
  readonly key: string;
  /** Whole percent, the measured cell's own value. */
  readonly value: number;
  readonly n: number;
}

/**
 * The stage a rung reaches only on the owner's record of a hand-over (`order.delivered`, stageMachine.ts),
 * not on a buyer moving on. TA-1361: it reads near 0% by construction wherever deliveries go unrecorded or
 * a made-to-order piece is still on the bench at the read, so, as G20 kept `consult_held` off the ladder,
 * a rung into it stays measured but is never named the largest drop.
 */
const STAGE_DROP_NOT_A_STEP = "delivered";

/**
 * Hypotheses are derived by rule from measured cells only, labelled, and kept
 * apart from the measured lines. Each names its n. Nothing here is a finding.
 */
export function deriveHypothesisCells(i: {
  readonly approval: readonly AggregateRow[];
  readonly stages: readonly AggregateRow[];
}): HypothesisCell[] {
  const out: HypothesisCell[] = [];
  const lowestOf = (rs: readonly AggregateRow[]): (AggregateRow & { value: number }) | undefined =>
    rs
      .filter((r): r is AggregateRow & { value: number } => r.status === "measured" && r.value !== null && r.value < 100)
      .sort((a, b) => a.value - b.value || a.key.localeCompare(b.key))[0];
  const lowest = lowestOf(i.approval);
  if (lowest !== undefined) out.push({ kind: "lowest_release", key: cellKey(lowest.key), value: lowest.value, n: lowest.n });
  // TA-1361 (B13): a rung enters only once its cohort holds the floor on the from side, whatever status
  // the row carries, and never a rung into the hand-over stage above.
  const drop = lowestOf(i.stages.filter((r) => r.n >= ANALYST_SAMPLE_FLOOR && r.key.split(">")[1] !== STAGE_DROP_NOT_A_STEP));
  if (drop !== undefined) out.push({ kind: "largest_stage_drop", key: cellKey(drop.key), value: drop.value, n: drop.n });
  return out;
}

/** The labelled line a hypothesis cell reads as. Every figure in it is the cell's own. */
export function hypothesisLine(c: HypothesisCell): string {
  return c.kind === "lowest_release"
    ? `Hypothesis, not tested: ${c.key} is released least often (${c.value}%, n=${c.n}); the drafts there may be missing a fact.`
    : `Hypothesis, not tested: the largest drop is ${c.key} (${c.value}% moved on, n=${c.n}); an association, not a cause.`;
}

export function deriveHypotheses(i: { readonly approval: readonly AggregateRow[]; readonly stages: readonly AggregateRow[] }): string[] {
  return deriveHypothesisCells(i).map(hypothesisLine);
}

// ------------------------------------------------------------------ the digest

export interface DigestSection {
  readonly id: AnalystQueryId;
  readonly title: string;
  readonly label: AggregateLabel;
  readonly n: number;
  readonly rows: readonly AggregateRow[];
}

export interface QueueSummary {
  /** Proposals waiting for the owner. */
  readonly pending: number;
  /** False when `learn.weekly` did not run this week. */
  readonly ran: boolean;
  /** A halt reason code when it did not run. */
  readonly whyNotRun?: string;
}

export interface AssistedFigure {
  readonly cents: number | null;
  readonly n: number;
  readonly withoutAmount: number;
}

export interface SectionCell {
  readonly key: string;
  readonly n: number;
  readonly value: number | null;
  readonly unit: AggregateUnit;
  readonly status: "measured" | "not_measured";
}

/**
 * The sections whose percent cells are rates of successes in n trials over exchangeable groups, so a
 * shrunken reading means something. Not `stage_conversion`: its cells are rungs of one funnel, nested
 * steps rather than exchangeable groups, so a prior pooled across them is the defect class of controller
 * Ruling 13 (TA-1360 feed, I4).
 */
const SHRUNK_SECTIONS: readonly AnalystQueryId[] = ["approval_rate_by_agent_scenario"];

/**
 * TA-1360 feed: a shrunken reading beside each rate cell, so "9 of 10" is not read as 90%.
 * Computed at read time from the cells a surface already has, never stored: `SectionCell` is
 * persisted inside the strict `analyst.reported` payload and stays as it is (controller Ruling 14).
 *
 * Index-aligned with `cells`. Only a measured percent-unit cell with n > 0 of
 * `approval_rate_by_agent_scenario` gets a reading (percent, one decimal):
 * successes are `Math.round(value * n / 100)` clamped to [0, n], and the prior is fitted over that
 * section's eligible cells (`fitBetaPrior`). Every other cell, and every other section, reads null.
 * With fewer than two eligible cells there is nothing to pool a prior from, so every cell reads
 * null: one group's own rate is not evidence about itself. The cells are the stored ones (at most
 * ANALYST_SECTION_CELLS_MAX per section), so the prior is fitted over those.
 */
export function shrunkReadings(sectionId: AnalystQueryId, cells: readonly SectionCell[]): readonly (number | null)[] {
  const none = cells.map(() => null);
  if (!SHRUNK_SECTIONS.includes(sectionId)) return none;
  const counts = cells.map((c): GroupCount | null => {
    if (c.status !== "measured" || c.unit !== "percent" || c.value === null || !(c.n > 0)) return null;
    const successes = Math.min(c.n, Math.max(0, Math.round((c.value * c.n) / 100)));
    return { successes, n: c.n };
  });
  const eligible = counts.filter((g): g is GroupCount => g !== null);
  if (eligible.length < 2) return none;
  const prior = fitBetaPrior(eligible);
  return counts.map((g) => (g === null ? null : Math.round(shrinkRate(g.successes, g.n, prior) * 1000) / 10));
}

/** A cell key the event catalog's detail string accepts: one line, trimmed, bounded, never empty. */
export function cellKey(key: string): string {
  const k = key.replace(/\s+/g, " ").trim().slice(0, 120).trim();
  return k === "" ? "unnamed" : k;
}

/** The most agents the mail line names by spend (G26). */
// AWAITING RATIFICATION
export const ANALYST_SPEND_TOP_AGENTS = 3;

/** One agent's model and tool spend over the closed week, versions folded together. */
export interface AgentSpendFigure {
  readonly agentId: string;
  /** Summed from calls that carry a price. A floor when `unpricedCalls > 0`. */
  readonly knownMicroUsd: number;
  /** Calls priced null: real calls whose cost nobody reported (TA-194). Never folded into zero. */
  readonly unpricedCalls: number;
}

/**
 * The gateway telemetry's spend over the closed week (G26; B12 per-agent cost meters). Known spend and
 * unpriced calls are TWO numbers, never one (TA-194, TA-197): a single total would either drop the unpriced
 * calls or count them as free, and both read a real charge as nothing.
 */
export interface AnalystSpend {
  readonly knownMicroUsd: number;
  readonly unpricedCalls: number;
  /** Every agent with a call in the window, ranked by known spend, then unpriced calls, then id. */
  readonly byAgent: readonly AgentSpendFigure[];
}

/** The mail line's spend tokens: the two figures apart, whether the known one is a floor, and the top agents. */
export function spendMailTokens(spend: AnalystSpend | null): string {
  if (spend === null) return " spend=not_available";
  const top = [...spend.byAgent]
    .sort((a, b) => b.knownMicroUsd - a.knownMicroUsd || b.unpricedCalls - a.unpricedCalls || a.agentId.localeCompare(b.agentId))
    .slice(0, ANALYST_SPEND_TOP_AGENTS)
    .map((a) => `${a.agentId.replace(/[^A-Za-z0-9_.-]/g, "_")}:${a.knownMicroUsd}/u${a.unpricedCalls}`)
    .join(",");
  return (
    ` spend_known_micro_usd=${spend.knownMicroUsd} unpriced_calls=${spend.unpricedCalls}` +
    ` spend_is_floor=${spend.unpricedCalls > 0}` +
    (top === "" ? "" : ` top_agents=${top}`)
  );
}

export interface AnalystDigestInput {
  /** The week's own Monday, `YYYY-MM-DD`. */
  readonly week: string;
  readonly periodStart: Date;
  readonly periodEnd: Date;
  readonly trailingDays: number;
  readonly headline: {
    /** `cents` is null below the floor: `amountN` is the rows with an amount, the n the sum rests on. */
    readonly deposits: { readonly count: number; readonly cents: number | null; readonly amountN: number; readonly withoutAmount: number };
    /** Consults held in the closed week, or null when not measured. */
    readonly consultsHeld: number | null;
    /**
     * Money on agent-assisted threads, deposit cents and sold cents KEPT APART (as the house
     * `tallyOutcomeCents` keeps one bucket per label). They are never added into one figure:
     * a deposit later counted inside a sale would be counted twice.
     * TA-W1-01, for Kabir: whether a combined figure is ever wanted, and how a deposit that is
     * later sold is treated. Until he rules, the conservative split stands.
     * Each figure's `n` is the rows WITH an amount; `cents` is null below the floor.
     */
    readonly assisted: { readonly deposit: AssistedFigure; readonly sold: AssistedFigure };
    readonly outcomesRecorded: number;
  };
  readonly sections: readonly DigestSection[];
  readonly hypotheses: readonly string[];
  /** G49: the same hypotheses as stored cells (`deriveHypothesisCells`), so the owner can read them later. */
  readonly hypothesisCells?: readonly HypothesisCell[];
  /** W7's rows, or null when W7 has reported nothing. */
  readonly scoreboard: readonly AggregateRow[] | null;
  /** W2's queue, or null when there is no loader. */
  readonly queue: QueueSummary | null;
  /** W5's confirm-or-correct count, or null. */
  readonly ownerConfirm: number | null;
  /** W4's triage count, or null. */
  readonly triage: number | null;
  /**
   * W integration 3: W7's own digest lines (`scoreboardDigestLines`), carried whole in place of the
   * one-line scoreboard summary when present; null or absent falls back to the summary line.
   */
  readonly scoreboardLines?: readonly string[] | null;
  /** W integration 3: W5's own confirm-or-correct section (`renderConfirmCorrectSection`), the same way. */
  readonly confirmCorrect?: readonly string[] | null;
  readonly personas: PersonaSynthesis | { readonly status: "off" };
  /**
   * G26: the closed week's gateway spend, or null when no reader had it (reads `spend=not_available`,
   * never zero). Absent leaves the mail line as it was.
   */
  readonly spend?: AnalystSpend | null;
}

export interface AnalystDigest {
  readonly week: string;
  readonly headline: readonly string[];
  readonly sections: readonly { readonly id: AnalystQueryId; readonly title: string; readonly lines: readonly string[] }[];
  readonly hypotheses: readonly string[];
  readonly carried: readonly string[];
  /** One line, counts and ids only. It begins with the phrase the worker's digest alarm matches. */
  readonly mailLine: string;
  /** The counts the `digest.analyst` event row records. */
  readonly counts: {
    readonly depositsCount: number;
    readonly depositsCents: number | null;
    readonly depositsAmountN: number;
    readonly depositsWithoutAmount: number;
    readonly consultsHeld: number | null;
    readonly assistedDepositCents: number | null;
    readonly assistedDepositN: number;
    readonly assistedSoldCents: number | null;
    readonly assistedSoldN: number;
    /** Rows with an amount across both labels (a count, not money), and the rows without one. */
    readonly revenueInfluencedN: number;
    readonly revenueInfluencedWithoutAmount: number;
    readonly sections: readonly {
      readonly id: AnalystQueryId;
      readonly label: AggregateLabel;
      readonly n: number;
      readonly rows: number;
      readonly notMeasured: number;
      readonly cells: readonly SectionCell[];
    }[];
    readonly hypotheses: number;
    /** G49: at most `ANALYST_HYPOTHESES_MAX` cells; codes and numbers only. */
    readonly hypothesisCells: readonly HypothesisCell[];
  };
}

/**
 * The start of the mail line: the phrase the digest alarm in
 * deploy/aws/alarms.tf matches. db's `DIGEST_LINE_PREFIX` is the source of
 * truth and a db test pins this copy equal to it.
 */
export const ANALYST_MAIL_PREFIX = "[digest] analyst";

function money(cents: number | null): string {
  return cents === null ? "not measured" : formatCents(cents);
}

/**
 * The weekly digest as a template (design W1: "Templated, no model"). Money
 * first, then the splits with their n, then hypotheses apart, then what the
 * other agents carried in. Every figure is a function of the input.
 */
export function buildAnalystDigest(i: AnalystDigestInput): AnalystDigest {
  const d = i.headline.deposits;
  const depositLine =
    d.count === 0
      ? `No deposit recorded this week (${i.headline.outcomesRecorded} outcomes recorded in all).`
      : d.cents === null
        ? `Deposits recorded this week: ${d.count} (n=${d.count}), amount not measured (n=${d.amountN}, needs ${ANALYST_SAMPLE_FLOOR})` +
          (d.withoutAmount > 0 ? `, ${d.withoutAmount} without an amount` : "") +
          "."
        : `Deposits recorded this week: ${d.count}, ${formatCents(d.cents)} in amount (n=${d.amountN})` +
          (d.withoutAmount > 0 ? `, ${d.withoutAmount} without an amount` : "") +
          ".";
  const consultLine =
    i.headline.consultsHeld === null
      ? "Consults held this week: not measured."
      : `Consults held this week: ${i.headline.consultsHeld} (n=${i.headline.consultsHeld}).`;
  const a = i.headline.assisted;
  const part = (f: AssistedFigure): string =>
    f.cents === null ? `not measured (n=${f.n}, needs ${ANALYST_SAMPLE_FLOOR})` : `${money(f.cents)} (n=${f.n})`;
  const assistedWithout = a.deposit.withoutAmount + a.sold.withoutAmount;
  const revenueLine =
    `Revenue on agent-assisted threads this week: deposits ${part(a.deposit)}, sold ${part(a.sold)}` +
    (assistedWithout > 0 ? `, ${assistedWithout} without an amount` : "") +
    ". Deposits and sold are kept apart. Assisted, an association, not a measured effect.";
  const headline = [depositLine, consultLine, revenueLine];

  const sections = i.sections.map((s) => ({
    id: s.id,
    title: `${s.title} (last ${i.trailingDays} days)`,
    lines: [...s.rows.map(renderRow), BASIS[s.label]],
  }));

  const carried: string[] = [];
  // W integration 3: W7's and W5's own lines are carried whole when a reader supplied them; the one-line
  // summaries stand in only when none did. Neither path invents a figure: W7's lines are counts as written.
  if (i.scoreboardLines !== undefined && i.scoreboardLines !== null && i.scoreboardLines.length > 0) carried.push(...i.scoreboardLines);
  else
    carried.push(
      i.scoreboard === null
        ? "Scoreboard: not available, no scoreboard report has been written."
        : i.scoreboard.length === 0
          ? "Scoreboard: reported no rows."
          : `Scoreboard: ${i.scoreboard.map(renderRow).join("; ")}`,
    );
  carried.push(
    i.queue === null
      ? "Learner queue: not available, no queue reader is connected."
      : !i.queue.ran
        ? `Learner queue: the learner did not run this week (${i.queue.whyNotRun ?? "no reason recorded"}).`
        : `Learner queue: ${i.queue.pending} proposal(s) waiting for you.`,
  );
  if (i.confirmCorrect !== undefined && i.confirmCorrect !== null && i.confirmCorrect.length > 0) carried.push(...i.confirmCorrect);
  else carried.push(i.ownerConfirm === null ? "Confirm or correct: not available." : `Confirm or correct: ${i.ownerConfirm} waiting.`);
  carried.push(i.triage === null ? "Triage: not available." : `Triage: ${i.triage} possible silent failure(s) to look at.`);
  const p = i.personas;
  if (p.status === "halted") {
    carried.push(
      p.reason === "below_thread_bar"
        ? `Personas: halted, ${p.n} of ${p.bar} labelled closed threads.`
        : `Personas: halted, the smallest group has ${p.n} of ${p.bar} threads.`,
    );
  } else if (p.status === "ok") {
    carried.push(`Personas: ${p.clusters.length} groups.`);
    for (const c of p.clusters) carried.push(`${c.id}: ${c.size} threads.`);
  } else {
    carried.push("Personas: off.");
  }

  const sectionCounts = i.sections.map((sec) => ({
    id: sec.id,
    label: sec.label,
    n: sec.n,
    rows: sec.rows.length,
    notMeasured: sec.rows.filter((r) => r.status === "not_measured").length,
    cells: sec.rows.slice(0, ANALYST_SECTION_CELLS_MAX).map(
      (r): SectionCell => ({ key: cellKey(r.key), n: r.n, value: r.value, unit: r.unit, status: r.status }),
    ),
  }));
  const counts = {
    depositsCount: d.count,
    depositsCents: d.cents,
    depositsAmountN: d.amountN,
    depositsWithoutAmount: d.withoutAmount,
    consultsHeld: i.headline.consultsHeld,
    assistedDepositCents: a.deposit.cents,
    assistedDepositN: a.deposit.n,
    assistedSoldCents: a.sold.cents,
    assistedSoldN: a.sold.n,
    revenueInfluencedN: a.deposit.n + a.sold.n,
    revenueInfluencedWithoutAmount: assistedWithout,
    sections: sectionCounts,
    hypotheses: i.hypotheses.length,
    hypothesisCells: (i.hypothesisCells ?? []).slice(0, ANALYST_HYPOTHESES_MAX).map((c) => ({ kind: c.kind, key: cellKey(c.key), value: c.value, n: c.n })),
  };
  const notMeasuredTotal = sectionCounts.reduce((acc, sec) => acc + sec.notMeasured, 0);
  const mailToken = (t: string): string => t.replace(/[^A-Za-z0-9_.:/>-]/g, "_");
  const mailCell = (c: SectionCell): string =>
    `${mailToken(c.key)}=${c.status === "measured" && c.value !== null ? c.value : "not_measured"}(n${c.n})`;
  const detail = sectionCounts
    .filter((sec) => sec.cells.length > 0)
    .map((sec) => {
      const shown = sec.cells.slice(0, ANALYST_MAIL_CELLS_MAX).map(mailCell).join(",");
      const more = sec.rows - Math.min(sec.cells.length, ANALYST_MAIL_CELLS_MAX);
      return `${sec.id}[${shown}${more > 0 ? `,+${more}` : ""}]`;
    })
    .join(";");
  const mailLine =
    `${ANALYST_MAIL_PREFIX} week=${i.week} deposits=${d.count} deposit_cents=${d.cents ?? "not_measured"}` +
    ` assisted_deposit_cents=${a.deposit.cents ?? "not_measured"}(n${a.deposit.n}) assisted_sold_cents=${a.sold.cents ?? "not_measured"}(n${a.sold.n})` +
    ` consults_held=${i.headline.consultsHeld ?? "not_measured"} sections=${i.sections.length}` +
    ` not_measured=${notMeasuredTotal} hypotheses=${i.hypotheses.length} personas=${p.status}` +
    (i.spend === undefined ? "" : spendMailTokens(i.spend)) +
    (detail === "" ? "" : ` detail=${detail}`);

  return { week: i.week, headline, sections, hypotheses: [...i.hypotheses], carried, mailLine, counts };
}
